import { useCallback, useEffect } from 'react';
import useSWR, { mutate } from 'swr';
import { Cart, Discount, DiscountErrorCode } from 'shared/types/cart';
import {
  AlertCode,
  CartResponse,
  RedeemDiscountPayload,
  RedeemDiscountResponse,
  RemoveDiscountPayload,
  RemoveDiscountResponse,
} from 'shared/types/checkout';
import { elementEvents, ElementType, ElementViewed } from 'helpers/eventing';
import { getProductsFromLineItems } from 'helpers/get-products-from-line-items';
import { makeSDKCallWithRetries } from 'helpers/makeSDKCallWithRetries';
import { centsToDollars, sumMoney } from 'helpers/priceHelpers';
import { sendGTMEvent } from 'helpers/sendGTMEvent';
import { withCookieValidation } from 'helpers/utils/cookie';
import { sdk } from 'sdk';
import {
  getAccountId,
  getIsCTADisabled,
  setAlert,
  useAppDispatch,
  useAppSelector,
} from 'store';
import { revalidateOptions } from '../options';

const getCart = (customerId: string) =>
  withCookieValidation<CartResponse>(async () =>
    makeSDKCallWithRetries(
      'cart/getCart',
      customerId,
      sdk.composableCommerce.cart.getCart,
    ),
  );

export const useCart = () => {
  const dispatch = useAppDispatch();
  const customerId = useAppSelector(getAccountId);
  const isCTADisabled = useAppSelector(getIsCTADisabled);
  const result = useSWR('/action/cart/getCart', () => getCart(customerId), {
    ...revalidateOptions,
    // Disable auto revalidation during order processing or coupon application.
    ...(isCTADisabled && { revalidateOnFocus: false }),
  });
  /* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
  const data = (
    result.data?.isError || result.data?.data?.isError ? {} : result.data?.data
  ) as Partial<Cart> | undefined;

  const isEmpty = data && 'lineItems' in data ? !data.lineItems?.length : true;
  const cartError = result.data?.isError
    ? result.data.error
    : result.data?.data?.isError
      ? result.data.data.error
      : null;

  const redeemDiscountCode = useCallback(
    (cartID: string) => async (code: string, skus: string[]) => {
      const payload: RedeemDiscountPayload = {
        cartID,
        code,
        skus,
      };
      const response = await withCookieValidation(() =>
        makeSDKCallWithRetries('cart/redeemDiscount', customerId, () =>
          sdk.callAction<RedeemDiscountResponse>({
            actionName: 'cart/redeemDiscount',
            payload,
          }),
        ),
      );
      let shouldRedirectToLegacyEcommerce = false;

      if (response.isError) {
        console.error(
          `Error in redeemDiscount extension for customer: ${customerId} for discount code: ${code}
          with error code: ${
            (response.error?.code as string) ?? ''
          } and error message: ${
            response.error?.message ??
            JSON.stringify(response.error) ??
            'unknown'
          }`,
        );
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
      } else if (response.data.isError) {
        if (response.data.error.code === 'cart_changed_error') {
          dispatch(setAlert({ code: AlertCode.CART_CHANGED }));
        } else if (
          response.data.error.code ===
            DiscountErrorCode.DOES_NOT_EXIST.toString() ||
          response.data.error.code ===
            DiscountErrorCode.DISCOUNT_CODE_NON_APPLICABLE.toString()
        ) {
          // Redirect to legacy e-commerce checkout page if discount is not found in CT.
          shouldRedirectToLegacyEcommerce = true;
        } else if (
          Object.values(AlertCode).includes(
            response.data.error.code as AlertCode,
          )
        ) {
          await mutate('/action/cart/getCart');
          dispatch(
            setAlert({
              code: response.data.error.code as AlertCode,
              value: code,
            }),
          );
        } else {
          await mutate('/action/cart/getCart');

          // Alert on applying an invalid coupon.
          dispatch(
            setAlert({
              code: AlertCode.FALLBACK,
              value: code,
            }),
          );
        }
        console.error(
          `Error in redeemDiscount extension for customer: ${customerId} for discount code: ${code}` +
            ` with error code: ${
              response.data.error.code ?? ''
            } and error message: ${
              response.data.error.message ??
              JSON.stringify(response.data.error) ??
              'unknown'
            }`,
        );
      } else if (response.data.code) {
        const { discountOnTotalPrice, lineItems, discountOnLineItems } =
          response.data.cart;
        sendGTMEvent({
          event: 'Coupon Applied',
          properties: {
            track_plan_id: 30,
            trigger_source: 'client-side',
            cart_id: cartID,
            checkout_id: cartID,
            coupon_name: code,
            discount: centsToDollars(
              sumMoney(discountOnTotalPrice, discountOnLineItems),
            ),
            currency: discountOnTotalPrice?.currencyCode ?? 'USD',
            products: getProductsFromLineItems(lineItems),
          },
        });

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        mutate(
          '/action/cart/getCart',
          { isError: response.data.isError ?? false, data: response.data.cart },
          {
            optimisticData: {
              isError: response.data.isError ?? false,
              data: response.data.cart,
            },
            revalidate: false,
            populateCache: true,
          },
        );
        dispatch(
          setAlert({
            code: AlertCode.REDEEM_DISCOUNT,
            value: response.data.code,
          }),
        );
        const paypalTagularElement: ElementViewed['webElement'] = {
          elementType: ElementType.Button,
          name: 'promotional-code',
          text: 'Apply',
        };

        const { elementViewed } = elementEvents({
          webElement: paypalTagularElement,
        });
        elementViewed();
      } else {
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
        console.error(
          `Error in redeemDiscount extension for customer: ${customerId} and discount code: ${code}.`,
        );
      }
      return shouldRedirectToLegacyEcommerce;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  const removeDiscountCode = useCallback(
    (cartID: string) => async (discount: Discount) => {
      const payload: RemoveDiscountPayload = {
        cartID,
        discount,
      };
      const response = await withCookieValidation(() =>
        sdk.callAction<RemoveDiscountResponse>({
          actionName: 'cart/removeDiscount',
          payload,
        }),
      );
      if (response.isError) {
        console.error(
          `Error in removeDiscount extension for customer: ${customerId} and discount code: ${discount.code!}` +
            ` with error code: ${
              (response.error?.code as string) ?? ''
            } and error message: ${
              response.error?.message ??
              JSON.stringify(response.error) ??
              'unknown'
            }`,
        );

        dispatch(setAlert({ code: AlertCode.FALLBACK }));
      } else if (response.data.isError) {
        if (response.data.error.code === 'cart_changed_error') {
          dispatch(setAlert({ code: AlertCode.CART_CHANGED }));
        } else {
          dispatch(setAlert({ code: AlertCode.FALLBACK }));
        }
        console.error(
          `Error in removeDiscount extension for customer: ${customerId} and discount code: ${discount.code!}` +
            ` with error code: ${
              response.data.error?.code ?? ''
            } error message: ${
              response.data.error.message ??
              JSON.stringify(response.data.error) ??
              'unknown'
            }`,
        );
      } else if (response.data.cart) {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        mutate(
          '/action/cart/getCart',
          { isError: response.data.isError ?? false, data: response.data.cart },
          {
            optimisticData: {
              isError: response.data.isError ?? false,
              data: response.data.cart,
            },
            revalidate: false,
            populateCache: true,
          },
        );
        dispatch(
          setAlert({
            code: AlertCode.REMOVE_DISCOUNT,
            value: discount.code,
          }),
        );
      } else {
        dispatch(setAlert({ code: AlertCode.FALLBACK }));
        console.error(
          `Error in removeDiscount extension for customer: ${customerId} and discount code: ${discount.code!}.`,
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId],
  );

  useEffect(() => {
    if (cartError && cartError.code !== 'ResourceNotFound') {
      console.error(
        `Error getting cart with product for customer: ${customerId} with error code: ${
          (cartError.code as string) ?? ''
        } and error message ${
          cartError.message ?? JSON.stringify(cartError) ?? 'unknown'
        }`,
      );
      dispatch(setAlert({ code: AlertCode.CART_LOAD_FAILED }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartError, customerId]);

  return {
    data,
    redeemDiscountCode,
    removeDiscountCode,
    isEmpty,
    isLoading: result.isLoading,
  };
};
